import { useCallback, useEffect, useState } from 'react';

import { faRotate, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/videa-components/src/ui/alert-dialog';
import { format, formatDistanceToNow } from 'date-fns';
import { useAtom, useSetAtom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import {
  type PendingRecoverySession,
  pendingRecoverySessionAtom,
  recordingSessionIdAtom,
} from '../atoms/recording';
import { type TranscriptData, opfs } from '../storage/opfs';

import { getAudioRecorder } from './microphone/audioRecorderControls';
import { CHUNK_INTERVAL_MS } from './microphone/AudioRecorderProvider';

/** Chunk interval in seconds for duration calculation */
const CHUNK_INTERVAL_SECONDS = CHUNK_INTERVAL_MS / 1000;

const debug = createDebugLogger('RecoveryModal');

interface RecordingRecoveryModalProps {
  /**
   * Called when the user chooses to resume the recording.
   * Receives the session metadata so the parent can restore appointment context.
   */
  onResume?: (session: PendingRecoverySession) => void;
  /**
   * Called when the user chooses to discard the recording.
   */
  onDiscard?: () => void;
}

/**
 * Format a timestamp to a human-readable date/time string.
 */
function formatRecordingDate(timestamp: number): string {
  const date = new Date(timestamp);
  const formattedDateTime = format(date, "EEE, MMM d 'at' h:mm a");
  const timeAgo = formatDistanceToNow(date, { addSuffix: true });

  return `${formattedDateTime} (${timeAgo})`;
}

/**
 * Modal dialog that appears when an interrupted recording is detected.
 * Allows the user to either resume or discard the recording.
 */
export const RecordingRecoveryModal = ({
  onResume,
  onDiscard,
}: RecordingRecoveryModalProps) => {
  const [pendingSession, setPendingSession] = useAtom(
    pendingRecoverySessionAtom,
  );
  const setSessionId = useSetAtom(recordingSessionIdAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptData, setTranscriptData] = useState<TranscriptData | null>(
    null,
  );

  // Load transcript data when session changes
  useEffect(() => {
    if (!pendingSession?.id) {
      setTranscriptData(null);
      return;
    }

    const loadTranscripts = async () => {
      try {
        debug.log(`Loading transcripts for session: ${pendingSession.id}`);
        const data = await opfs.getTranscripts(pendingSession.id);
        debug.log(
          `Loaded transcript data:`,
          data
            ? {
                transcriptCount: data.transcripts.length,
                totalCharacters: data.totalCharacters,
                hasContent: data.transcripts.length > 0,
              }
            : 'No data',
        );
        setTranscriptData(data);
      } catch (error) {
        debug.error('Failed to load transcript data:', error);
        // Set empty data instead of null to show proper fallback message
        setTranscriptData({
          transcripts: [],
          lastUpdated: Date.now(),
          totalCharacters: 0,
        });
      }
    };

    loadTranscripts();
  }, [pendingSession?.id]);

  const handleDiscard = useCallback(async () => {
    if (!pendingSession) return;

    setIsLoading(true);
    try {
      await opfs.deleteSession(pendingSession.id);
      onDiscard?.();
    } catch (error) {
      debug.error('Failed to delete session:', error);
    } finally {
      setPendingSession(null);
      setIsLoading(false);
    }
  }, [pendingSession, onDiscard, setPendingSession]);

  const handleResume = useCallback(async () => {
    if (!pendingSession) return;

    setIsLoading(true);
    const sessionIdToResume = pendingSession.id;

    try {
      // Microphone permission is checked automatically by MicrophoneProvider on mount.
      // Since the user recorded in a previous session, they must have granted permission.

      // Update OPFS session status back to recording
      await opfs.updateSessionStatus(sessionIdToResume, 'recording');

      // Save session metadata before clearing
      const sessionMetadata = { ...pendingSession };

      // Clear the modal first
      setPendingSession(null);

      // Start recording - pass session ID directly to avoid race conditions
      // The AudioRecorderProvider will use this to resume from the correct session
      getAudioRecorder().start?.(sessionIdToResume);

      // Also set the atom for consistency (after start is called)
      setSessionId(sessionIdToResume);

      // Pass session metadata to parent so it can restore appointment context
      onResume?.(sessionMetadata);
    } catch (error) {
      debug.error('Failed to resume recording:', error);
      // On error, just discard and let user try again
      await handleDiscard();
    } finally {
      setIsLoading(false);
    }
  }, [
    pendingSession,
    onResume,
    setPendingSession,
    handleDiscard,
    setSessionId,
  ]);

  if (!pendingSession) {
    return null;
  }

  return (
    <AlertDialog open={!!pendingSession}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Recover Interrupted Recording?</AlertDialogTitle>
          <AlertDialogDescription>
            We found an unfinished recording from{' '}
            <span className="vai-font-medium">
              {formatRecordingDate(pendingSession.createdAt)}
            </span>
            . Would you like to resume where you left off or start fresh?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="vai-py-2 vai-text-sm vai-text-muted-foreground vai-space-y-2">
          <p>
            Recording duration:{' '}
            <span className="vai-font-medium">
              ~
              {Math.ceil(
                (pendingSession.chunkCount * CHUNK_INTERVAL_SECONDS) / 60,
              )}{' '}
              minute
              {Math.ceil(
                (pendingSession.chunkCount * CHUNK_INTERVAL_SECONDS) / 60,
              ) === 1
                ? ''
                : 's'}
            </span>
            {transcriptData && transcriptData.totalCharacters > 0 && (
              <span>
                {' '}
                ({transcriptData.totalCharacters.toLocaleString()} characters
                transcribed)
              </span>
            )}
          </p>
          {transcriptData && transcriptData.transcripts.length > 0 ? (
            <div className="vai-border-l-2 vai-border-muted vai-pl-2 vai-space-y-1 vai-max-h-32 vai-overflow-y-auto">
              <div className="vai-italic vai-text-xs vai-space-y-1">
                {transcriptData.transcripts
                  .filter((t) => !t.interim && t.text.trim())
                  .slice(-5) // Show last 5 transcript segments
                  .map((transcript, index) => (
                    <p key={transcript.timestamp + index} className="vai-mb-1">
                      &quot;{transcript.text}&quot;
                    </p>
                  ))}
              </div>
              <p className="vai-text-[10px] vai-text-muted-foreground/70">
                Voice transcription preview — final transcription may differ
              </p>
            </div>
          ) : pendingSession.lastTranscriptContext ? (
            <div className="vai-border-l-2 vai-border-muted vai-pl-2 vai-space-y-1">
              <p className="vai-italic vai-text-xs">
                &quot;...{pendingSession.lastTranscriptContext}&quot;
              </p>
              <p className="vai-text-[10px] vai-text-muted-foreground/70">
                Rough preview only — final transcription may differ
              </p>
            </div>
          ) : (
            <div className="vai-text-xs vai-text-muted-foreground/80">
              <p>Audio recording is available for recovery.</p>
              <p className="vai-text-[10px] vai-mt-1">
                Transcript preview unavailable — audio will be processed after
                recovery
              </p>
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleDiscard} disabled={isLoading}>
            <FontAwesomeIcon icon={faTrash} className="vai-mr-2" />
            Discard
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleResume}
            disabled={isLoading}
            className="vai-bg-primary vai-text-primary-foreground hover:vai-bg-primary/90"
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={`vai-mr-2 ${isLoading ? 'vai-animate-spin' : ''}`}
            />
            {isLoading ? 'Recovering...' : 'Resume Recording'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
